import {
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react'
import { IconArrowForwardUp as AskAgain } from '@tabler/icons-react'
import React, { useEffect, useRef, useState } from 'react'
import { AGENT_COMMAND, MAIN_CHAT_PICK } from '../../core/parsers/slashCommandParser'
import { useCommands } from '../../hooks/commands/useCommands'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { ReferredTo } from '../../types/chatTypes'
import { getAgentIcon } from '../AgentIcon'

interface AskAgainMenuProps {
  // The agent that wrote this answer: not offered again.
  authorId: string
  // The assistant the answer sends the user to: it gets its own button.
  referredTo?: ReferredTo
  onPick: (pick: string) => void
  isDisabled?: boolean
}

// Sends the same question to another assistant. The menu (the `@` options) and
// the referral (from the server) hold only assistants the user may reach; the
// server checks the pick again.
export const AskAgainMenu: React.FC<AskAgainMenuProps> = ({ authorId, referredTo, onPick, isDisabled }) => {
  const { t } = useLanguage()
  const iconColor = useColorModeValue('gray.400', 'gray.300')
  const { commands } = useCommands()
  const options = commands.find(command => command.name === AGENT_COMMAND)?.options.filter(o => o.value !== authorId)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const listRef = useRef<HTMLDivElement>(null)
  // Mouse hover only: focus returns to the button when the menu closes, and a
  // focus-shown tooltip would then float over the composer.
  const [isHovered, setIsHovered] = useState(false)

  // Any scroll of the chat (wheel, trackpad, scrollbar, keys) closes the menu
  // and its tooltip, so neither floats over the composer.
  const isShowing = isOpen || isHovered
  useEffect(() => {
    if (!isShowing) return
    const onScroll = (event: Event) => {
      if (listRef.current?.contains(event.target as Node)) return
      setIsHovered(false)
      onClose()
    }
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [isShowing, onClose])

  if (!options?.length && !referredTo) return null
  const referredIcon = options?.find(option => option.value === referredTo?.value)?.icon

  return (
    <>
      {referredTo && (
        <Button
          size='sm'
          variant='ghost'
          leftIcon={getAgentIcon(referredIcon, 16, referredTo.value !== MAIN_CHAT_PICK)}
          color='chat.brand'
          bg='chat.selected'
          _hover={{ bg: 'chat.selected', opacity: 0.85 }}
          isDisabled={isDisabled}
          onClick={() => onPick(referredTo.value)}
        >
          {t('chat.askAgent', { name: referredTo.name })}
        </Button>
      )}
      {/* Opens upwards, away from the composer; the portal keeps it out of the chat's clipping. */}
      {!!options?.length && (
        <Menu placement='top-start' isLazy isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
          <Tooltip label={t('chat.askAgain')} placement='top' hasArrow isOpen={isHovered && !isOpen}>
            <MenuButton
              as={IconButton}
              icon={<AskAgain size={18} />}
              size='sm'
              variant='ghost'
              color={iconColor}
              aria-label={t('chat.askAgain')}
              isDisabled={isDisabled}
              // A mouse only: a tap fires an enter without a leave.
              onPointerEnter={event => event.pointerType === 'mouse' && setIsHovered(true)}
              onPointerLeave={() => setIsHovered(false)}
            />
          </Tooltip>
          <Portal>
            <MenuList ref={listRef} minW='200px' maxW='280px'>
              {options.map(option => (
                <MenuItem
                  key={option.value}
                  icon={getAgentIcon(option.icon, 16, option.value !== MAIN_CHAT_PICK)}
                  fontSize='sm'
                  onClick={() => onPick(option.value)}
                >
                  <Text noOfLines={1}>{option.name}</Text>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>
      )}
    </>
  )
}
